import { useState, useEffect, useRef, useCallback } from "react";
import { X, ArrowRight, Gift, CheckCircle2 } from "lucide-react";
import PhoneInput, { getPhoneWithCode } from "@/components/PhoneInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOBILE_BREAKPOINT = 768;
const SCROLL_UP_THRESHOLD = 100;
const NEAR_TOP_THRESHOLD = 200;

export default function ExitIntentPopup() {
  const [show, setShow] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "" });
  const triggered = useRef(false);
  const timeoutRef = useRef(null);
  const [dialCode, setDialCode] = useState(null);

  const triggerPopup = useCallback(() => {
    if (triggered.current) return;
    if (sessionStorage.getItem("exit_popup_shown")) return;
    const path = window.location.pathname;
    if (path.startsWith("/admin") || path.startsWith("/dashboard") || path.startsWith("/login")) return;
    triggered.current = true;
    sessionStorage.setItem("exit_popup_shown", "1");
    setShow(true);
  }, []);

  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0) triggerPopup();
    };

    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      const currentY = window.scrollY;
      if (currentY < lastScrollY - SCROLL_UP_THRESHOLD && currentY < NEAR_TOP_THRESHOLD && window.innerWidth < MOBILE_BREAKPOINT) {
        triggerPopup();
      }
      lastScrollY = currentY;
    };

    // Delay attaching listeners so popup doesn't fire on page load
    timeoutRef.current = setTimeout(() => {
      document.addEventListener("mouseleave", handleMouseLeave);
      window.addEventListener("scroll", handleScroll, { passive: true });
    }, 8000);

    return () => {
      clearTimeout(timeoutRef.current);
      document.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [triggerPopup]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) return;
    setLoading(true);
    try {
      const pathParts = window.location.pathname.split('/');
      const country = pathParts[1] || "in";
      const pagePath = pathParts.slice(2).join('/') || "home";
      await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, phone: getPhoneWithCode(form.phone, country, dialCode), message: "Exit-intent popup lead", country, source_page: `Exit Intent Popup (on /${pagePath})` }),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const close = () => setShow(false);

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="exit-intent-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={close} />

      {/* Popup */}
      <div
        className="relative w-full max-w-md bg-gradient-to-b from-zinc-900 to-zinc-950 border border-zinc-800 rounded-2xl shadow-2xl shadow-purple-500/10 animate-in zoom-in-95 fade-in duration-300"
        data-testid="exit-intent-popup"
      >
        <button
          onClick={close}
          className="absolute top-3 right-3 p-1.5 rounded-full text-zinc-500 hover:text-white hover:bg-white/10 transition-colors z-10"
          data-testid="exit-intent-close"
        >
          <X className="w-4 h-4" />
        </button>

        {!submitted ? (
          <div className="p-6 sm:p-8">
            {/* Header */}
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1.5 bg-amber-500/10 rounded-lg">
                <Gift className="w-4 h-4 text-amber-400" />
              </div>
              <span className="text-xs font-semibold tracking-widest uppercase text-amber-400">
                Wait — Free Offer
              </span>
            </div>

            <h2 className="text-xl sm:text-2xl font-bold text-white mt-3 leading-tight">
              Get a <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-amber-400">Free 30-Min</span> Strategy Call
            </h2>
            <p className="text-zinc-400 text-sm mt-2 leading-relaxed">
              Before you go — our experts will audit your business and share a custom growth roadmap. No cost, no strings attached.
            </p>

            {/* Form */}
            <form onSubmit={handleSubmit} className="mt-5 space-y-3">
              <input
                type="text"
                placeholder="Your Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-2.5 bg-white/5 border border-zinc-700 rounded-lg text-white text-sm placeholder:text-zinc-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500/30 transition-all"
                required
                data-testid="exit-intent-name"
              />
              <PhoneInput value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} countryCode={window.location.pathname.split('/')[1] || "in"} testId="exit-intent-phone" onDialChange={setDialCode} />

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold text-black bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 transition-all disabled:opacity-60"
                data-testid="exit-intent-submit"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                ) : (
                  <>
                    Claim Free Strategy Call
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <p className="text-center text-[11px] text-zinc-600 mt-3">
              100% free. No spam. We respect your privacy.
            </p>
          </div>
        ) : (
          /* Success state */
          <div className="p-8 text-center" data-testid="exit-intent-success">
            <div className="mx-auto w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-6 h-6 text-green-400" />
            </div>
            <h3 className="text-lg font-bold text-white">You&apos;re All Set!</h3>
            <p className="text-zinc-400 text-sm mt-2">
              Our team will reach out within 24 hours to schedule your free strategy call.
            </p>
            <button
              onClick={close}
              className="mt-5 px-6 py-2.5 rounded-lg text-sm font-medium text-white bg-white/10 hover:bg-white/15 transition-colors"
              data-testid="exit-intent-done"
            >
              Continue Browsing
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
